import { Box, Button, FormHelperText, InputLabel, Stack } from '@mui/material';
import { type ReactNode, useId, useState } from 'react';

import { ConfirmationDialog, Text, isDefined } from 'common';

import { BlockKeyValueRow } from './Row';
import { BlockKeyValuesTableHeader } from './TableHeader';
import { type KeyValueData, type KeyValueFieldNames } from './types';
import { type ConfigFieldProps } from '../types';
import { useSelectedNodeField } from '../useSelectedNodeField';

interface BlockKeyValuesTableBaseProps extends ConfigFieldProps {
  helperText?: ReactNode;
  addRowHelperText: string;
  keyValueFieldNames: KeyValueFieldNames;
  onToggleFullScreen: () => void;
  onAddRow: () => void;
}

interface BlockKeyValuesTableInnerProps extends BlockKeyValuesTableBaseProps {
  labelId: string;
}

interface BlockKeyValuesTableProps extends BlockKeyValuesTableBaseProps {
  label: ReactNode;
  isFullScreen: boolean;
}

function BlockKeyValuesTableInner(props: BlockKeyValuesTableInnerProps) {
  const {
    fieldName,
    labelId,
    helperText,
    addRowHelperText,
    keyValueFieldNames,
    onAddRow,
    onToggleFullScreen,
    ...fieldProps
  } = props;

  const { setValue, value: values, visibleError } = useSelectedNodeField<KeyValueData[]>(fieldName);

  if (!isDefined(values) || values.length === 0) {
    return (
      <Box>
        <Button variant="outlined" onClick={() => onAddRow()}>{`+ Add ${addRowHelperText}`}</Button>
      </Box>
    );
  }

  return (
    <Stack aria-labelledby={labelId} gap={2}>
      <Box border="1px solid" borderColor="rgba(0, 0, 0, 0.23)" borderRadius="4px">
        <Stack direction="row" gap={2} sx={{ backgroundColor: '#f7f7f7' }}>
          <Text variant="heading6" bold flex={5} p="6.5px 14px">
            {keyValueFieldNames.key.label}
          </Text>
          <Text variant="heading6" bold flex={5} p="6.5px 0px">
            {keyValueFieldNames.value.label}
          </Text>
          <Box flex={1} />
        </Stack>
        {values?.map((value, index) => (
          <BlockKeyValueRow
            {...fieldProps}
            key={value.id ?? index}
            fieldName={`${fieldName}.${index}`}
            rowIndex={index}
            keyValueFieldNames={keyValueFieldNames}
            onDelete={() => setValue(values.filter((_, i) => i !== index))}
          />
        ))}
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {visibleError && (
          <Text variant="subtitle2" color="error.main" bold>
            {visibleError}
          </Text>
        )}
      </Stack>
      <Button
        sx={{ alignSelf: 'flex-start' }}
        variant="outlined"
        onClick={() => onAddRow()}
      >{`+ Add ${addRowHelperText}`}</Button>
    </Stack>
  );
}

export function BlockKeyValuesTable(props: BlockKeyValuesTableProps) {
  const { helperText, label, fieldName } = props;
  const { setValue } = useSelectedNodeField<KeyValueData[]>(fieldName);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

  const labelId = useId();
  return (
    <>
      <Stack spacing={0.5}>
        <Stack
          role="group"
          aria-labelledby={labelId}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <InputLabel id={labelId}>{label}</InputLabel>
          <BlockKeyValuesTableHeader
            {...props}
            setIsConfirmationDialogOpen={setIsConfirmationDialogOpen}
          />
        </Stack>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
        <BlockKeyValuesTableInner {...props} labelId={labelId} />
      </Stack>

      <ConfirmationDialog
        title="Clear All Values"
        onClose={() => setIsConfirmationDialogOpen(false)}
        open={isConfirmationDialogOpen}
        actions={
          <>
            <Button
              onClick={() => {
                setIsConfirmationDialogOpen(false);
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setValue([]);
                setIsConfirmationDialogOpen(false);
              }}
            >
              Clear All
            </Button>
          </>
        }
      >
        Are you sure you want to clear all values?
      </ConfirmationDialog>
    </>
  );
}
